/**
 * Article view – X-style read: back + "Article" + Reply, author block,
 * cover image, title, engagement bar, body. Drafts show "Only you can view" banner.
 */
import {useCallback} from 'react'
import {Image, ScrollView, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {
  useStudioArticleQuery,
  useStudioUpdateArticle,
  useStudioDeleteArticle,
} from '#/state/queries/studio'
import {useProfileQuery} from '#/state/queries/profile'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Menu from '#/components/Menu'
import {DotGrid_Stroke2_Corner0_Rounded as EllipsisIcon} from '#/components/icons/DotGrid'
import {Pencil_Stroke2_Corner0_Rounded as PencilIcon} from '#/components/icons/Pencil'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import {Reply as ReplyIcon} from '#/components/icons/Reply'
import * as Layout from '#/components/Layout'
import * as Prompt from '#/components/Prompt'
import {Text} from '#/components/Typography'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {sanitizeHandle} from '#/lib/strings/handles'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {STUDIO_API_BASE} from '#/lib/api/studio'
import type {CommonNavigatorParams} from '#/lib/routes/types'
import {ArticleBodyView} from '#/screens/Articles/ArticleBodyView'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Article'>

const ARTICLE_MAX_WIDTH = 720

export function ArticleScreen({route, navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const {articleId} = route.params

  const {data: article, isLoading} = useStudioArticleQuery(articleId)
  const {data: authorProfile} = useProfileQuery({
    did: article?.owner_did ?? '',
  })
  const updateArticle = useStudioUpdateArticle()
  const deleteArticle = useStudioDeleteArticle()
  const deletePromptControl = Prompt.usePromptControl()

  const onEdit = useCallback(() => {
    navigation.navigate('ArticleEditor', {articleId})
  }, [navigation, articleId])

  const onDeleteConfirm = useCallback(async () => {
    deletePromptControl.close()
    try {
      await deleteArticle.mutateAsync(articleId)
      navigation.navigate('Articles')
    } catch {
      // Error surfaced by mutation
    }
  }, [articleId, deleteArticle, navigation, deletePromptControl])

  const onUnpublishAndEdit = useCallback(async () => {
    try {
      await updateArticle.mutateAsync({
        id: articleId,
        input: {status: 'draft'},
      })
      navigation.navigate('ArticleEditor', {articleId})
    } catch {
      // Error surfaced by mutation
    }
  }, [articleId, updateArticle, navigation])

  if (isLoading || !article) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.MenuButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Article</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {isLoading ? (
              <Trans>Loading…</Trans>
            ) : (
              <Trans>Article not found</Trans>
            )}
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  const isDraft = article.status === 'draft'
  const coverImageUrl =
    article.cover_image_path?.startsWith('http') === true
      ? article.cover_image_path
      : article.cover_image_path
        ? `${STUDIO_API_BASE}${article.cover_image_path.startsWith('/') ? '' : '/'}${article.cover_image_path}`
        : null

  return (
    <Layout.Screen>
      {/* Header: back | Article (centered) | Reply + 3-dot */}
      <Layout.Header.Outer>
        <Layout.Header.MenuButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Article</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Button
              label={_(msg`Reply`)}
              color="secondary"
              size="small"
              onPress={() => {}}>
              <ButtonIcon icon={ReplyIcon} position="left" />
              <ButtonText>
                <Trans>Reply</Trans>
              </ButtonText>
            </Button>
            <Menu.Root>
              <Menu.Trigger label={_(msg`Article options`)}>
                {({props}) => (
                  <Button
                    {...props}
                    label={_(msg`Article options`)}
                    color="secondary"
                    size="small"
                    shape="round">
                    <ButtonIcon icon={EllipsisIcon} />
                  </Button>
                )}
              </Menu.Trigger>
              <Menu.Outer>
                <Menu.Group>
                  <Menu.Item
                    label={_(msg`Edit Article`)}
                    onPress={onUnpublishAndEdit}>
                    <Menu.ItemIcon icon={PencilIcon} />
                    <Menu.ItemText>
                      <Trans>Edit Article</Trans>
                    </Menu.ItemText>
                  </Menu.Item>
                  <Menu.Item
                    label={_(msg`Delete`)}
                    onPress={() => deletePromptControl.open()}>
                    <Menu.ItemIcon icon={TrashIcon} />
                    <Menu.ItemText>
                      <Trans>Delete</Trans>
                    </Menu.ItemText>
                  </Menu.Item>
                </Menu.Group>
              </Menu.Outer>
            </Menu.Root>
          </View>
        </Layout.Header.Slot>
      </Layout.Header.Outer>

      <View
        style={[
          a.flex_1,
          a.w_full,
          gtMobile && web({maxWidth: ARTICLE_MAX_WIDTH, alignSelf: 'center'}),
        ]}>
      <ScrollView
        style={[a.flex_1]}
        contentContainerStyle={[a.pb_2xl]}
        keyboardShouldPersistTaps="handled">
        {/* Cover image */}
        {coverImageUrl ? (
          <View style={[a.overflow_hidden, {aspectRatio: 5 / 2}]}>
            <Image
              source={{uri: coverImageUrl}}
              style={[a.w_full, a.h_full, {resizeMode: 'cover'}]}
              accessibilityLabel={_(msg`Article cover image`)}
            />
            {isDraft && (
              <View
                style={[
                  a.absolute,
                  {top: 12, right: 12},
                  a.rounded_full,
                  a.px_sm,
                  a.py_2xs,
                  {backgroundColor: t.palette.primary_500},
                ]}>
                <Text style={[a.text_xs, a.font_semi_bold, {color: '#fff'}]}>
                  <Trans>Draft</Trans>
                </Text>
              </View>
            )}
          </View>
        ) : null}

        {/* Draft banner */}
        {isDraft && (
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.justify_center,
              a.p_md,
              a.mx_lg,
              a.mt_md,
              a.rounded_md,
              {backgroundColor: t.palette.primary_500},
            ]}>
            <Text style={[a.text_sm, a.font_semi_bold, {color: '#fff'}]}>
              <Trans>Only you can view this unpublished Article.</Trans>
            </Text>
            <Button
              label={_(msg`Edit`)}
              color="secondary_inverted"
              size="tiny"
              onPress={onEdit}
              style={a.ml_md}>
              <ButtonText>
                <Trans>Edit</Trans>
              </ButtonText>
            </Button>
          </View>
        )}

        <View style={[a.px_lg, a.pt_lg]}>
          {/* Author */}
          <View style={[a.flex_row, a.align_center, a.gap_sm, a.pb_md]}>
            <UserAvatar
              avatar={authorProfile?.avatar}
              size={40}
              type="user"
              hideLiveBadge
            />
            <View style={[a.flex_1, a.min_w_0]}>
              <Text
                style={[a.text_md, a.font_semi_bold, t.atoms.text]}
                numberOfLines={1}>
                {sanitizeDisplayName(authorProfile?.displayName ?? '')}
              </Text>
              <Text
                style={[a.text_sm, t.atoms.text_contrast_medium]}
                numberOfLines={1}>
                @{sanitizeHandle(authorProfile?.handle ?? article.owner_did ?? '', '')}
              </Text>
            </View>
          </View>

          {/* Title */}
          <Text
            style={[a.text_2xl, a.font_bold, a.pb_md, t.atoms.text]}
            selectable>
            {article.title || _(msg`(Untitled)`)}
          </Text>

          {/* Engagement bar (placeholder) */}
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.gap_lg,
              a.pb_lg,
              a.border_b,
              t.atoms.border_contrast_low,
            ]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              ♻️ <Trans>Repost</Trans>
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              ♥ <Trans>Like</Trans>
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              📊 <Trans>Analytics</Trans>
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              🔖 <Trans>Bookmark</Trans>
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              ↗ <Trans>Share</Trans>
            </Text>
          </View>

          {/* Body – HTML from TinyMCE on web, plain text on native */}
          <ArticleBodyView content={article.content ?? ''} />
        </View>
      </ScrollView>
      </View>

      <Prompt.Outer control={deletePromptControl}>
        <Prompt.TitleText>
          <Trans>Delete Article?</Trans>
        </Prompt.TitleText>
        <Prompt.DescriptionText>
          <Trans>This cannot be undone.</Trans>
        </Prompt.DescriptionText>
        <Prompt.Actions>
          <Prompt.Cancel />
          <Prompt.Action
            onPress={onDeleteConfirm}
            cta={_(msg`Delete`)}
            color="negative"
          />
        </Prompt.Actions>
      </Prompt.Outer>
    </Layout.Screen>
  )
}
